/**
 * The team profile Roster panel, ISSUE-1168. Reads sports-api POST /team/roster-get through useTeamRosterGet and owns that read, so a fan who never opens Roster never pays for it (Tabs unmounts an inactive panel, see TeamStatsTab.tsx). Rows link to /scores/player with the params LineupTab.tsx sends.
 */
import { useTeamRosterGet } from "@sportsmet/sports-sdk/private";
import { FOCUS_RING, Skeleton } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { PlayerHeadshot } from "../playerProfile/PlayerHeadshot";
import { playerJerseyLabel } from "../playerProfile/playerProfileView";
import { TeamReadFailed } from "./TeamReadFailed";
import {
  TEAM_ROSTER_EMPTY_LINE,
  TEAM_ROSTER_FAILED_LINE,
  teamRosterPlayerSearch,
  toTeamRosterSections,
} from "./teamRosterLogic";

export const TeamRosterTab = (props: {
  teamId: string;
  teamName: string;
  teamAbbreviation: string;
}): ReactNode => {
  const read = useTeamRosterGet({ teamId: props.teamId });

  useEffect(() => {
    if (extractErrorCode(read.data) !== null) {
      showApiError(read.data, undefined, read.dataUpdatedAt);
    }
  }, [read.dataUpdatedAt, props.teamId]);

  const failed = (
    <TeamReadFailed
      layout="panel"
      testId="team-roster-failed"
      line={TEAM_ROSTER_FAILED_LINE}
      onRetry={() => {
        void read.refetch();
      }}
    />
  );

  if (extractErrorCode(read.data) !== null) return failed;

  const roster = read.data?.data;
  if (roster === undefined || roster === null) {
    if (read.isPending) {
      return (
        <div data-testid="team-roster-loading">
          <Skeleton shape="card" />
        </div>
      );
    }
    return failed;
  }

  const sections = toTeamRosterSections(roster.groups);

  return (
    <div className="flex flex-col gap-4" data-testid="team-roster-tab">
      {sections.length === 0 ? (
        <p
          data-testid="team-roster-empty"
          className="text-body text-ink-secondary"
        >
          {roster.emptyReason ?? TEAM_ROSTER_EMPTY_LINE}
        </p>
      ) : (
        sections.map((section) => (
          // One card per position group (Scores UX spec v1.0 section 8).
          <section
            key={section.groupKey}
            className="flex flex-col overflow-hidden rounded-md border border-hairline bg-surface pt-3"
            data-testid="team-roster-section"
          >
            <h3
              data-testid="team-roster-section-heading"
              className="px-4 pb-2 text-body-strong text-ink"
            >
              {section.label}
            </h3>
            <ul className="flex flex-col">
              {section.players.map((player) => {
                const jerseyLabel = playerJerseyLabel(player.jerseyNumber);
                return (
                  <li key={player.playerId}>
                    <Link
                      to="/scores/player"
                      search={teamRosterPlayerSearch({
                        player,
                        team: {
                          teamId: props.teamId,
                          name: props.teamName,
                          abbreviation: props.teamAbbreviation,
                        },
                      })}
                      data-testid="team-roster-row"
                      className={`flex min-h-12 items-center gap-3 border-t border-hairline px-4 py-1 text-ink no-underline transition-colors duration-200 hover:bg-surface-sunken motion-reduce:transition-none ${FOCUS_RING}`}
                    >
                      <PlayerHeadshot
                        headshotUrl={player.headshotUrl}
                        size="md"
                      />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span
                          data-testid="team-roster-row-name"
                          className="truncate text-body-strong text-ink"
                        >
                          {player.name}
                        </span>
                        {jerseyLabel !== null ? (
                          <span
                            data-testid="team-roster-row-number"
                            className="text-small text-ink-secondary"
                          >
                            {jerseyLabel}
                          </span>
                        ) : null}
                      </span>
                      {player.position !== null ? (
                        <span
                          data-testid="team-roster-row-position"
                          className="text-caption text-ink-secondary"
                        >
                          {player.position}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  );
};
